import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { getPaymentRequests, getFiscalYears } from '../services/api';
import { useToken } from '../hooks/useToken';
import type { PaymentRequest, StatusName, FiscalYear } from '../types';

const STATUS_OPTIONS: Array<StatusName | ''> = [
  '', 'Pending BHSD Approval', 'Pending Onyx Approval', 'Pending Payment', 'Paid', 'Rejected',
];

const PAGE_SIZE = 20;

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—';
  return new Date(s.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function RequestsList() {
  const getToken = useToken();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<PaymentRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);

  const search = searchParams.get('search') ?? '';
  const status = searchParams.get('status') ?? '';
  const fiscalYearId = searchParams.get('fiscalYearId') ? parseInt(searchParams.get('fiscalYearId')!, 10) : undefined;
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const payeeId = searchParams.get('payee_id') ? parseInt(searchParams.get('payee_id')!, 10) : undefined;

  const [searchInput, setSearchInput] = useState(search);

  // Debounce search input
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function setParam(key: string, val: string) {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }

  function handleSearchInput(val: string) {
    setSearchInput(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setParam('search', val), 350);
  }

  // Load fiscal years once for the filter dropdown
  useEffect(() => {
    getToken()
      .then((t) => getFiscalYears(t))
      .then(setFiscalYears)
      .catch(() => {/* non-fatal */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getToken()
      .then((t) =>
        getPaymentRequests(t, {
          search: search || undefined,
          status: status || undefined,
          payee_id: payeeId,
          fiscalYearId,
          page,
          pageSize: PAGE_SIZE,
        }),
      )
      .then((res) => {
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, fiscalYearId, page, payeeId]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Layout pageTitle="Requests">
      {/* Payee filter banner */}
      {payeeId && (
        <div className="flex items-center justify-between mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <span>Showing requests for selected payee</span>
          <button
            onClick={() => setParam('payee_id', '')}
            className="text-blue-600 hover:text-blue-800 font-medium ml-4"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <input
          type="text"
          value={searchInput}
          placeholder="Search by Request #, Payee, Fund Pool, BHSD Approver..."
          onChange={(e) => handleSearchInput(e.target.value)}
          className="flex-1 min-w-48 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {fiscalYears.length > 0 && (
          <select
            value={fiscalYearId ?? ''}
            onChange={(e) => setParam('fiscalYearId', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Fiscal Years</option>
            {fiscalYears.map((fy) => (
              <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>{fy.label}</option>
            ))}
          </select>
        )}
        <select
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        {error ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No requests found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Request #</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payee</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fund Pool / Project</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">BHSD Approver</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">BHSD Approval Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.payment_request_id}
                  onClick={() => navigate(`/requests/${r.payment_request_id}`)}
                  className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer last:border-0"
                >
                  <td className="px-5 py-3 font-mono text-xs text-gray-700">{r.request_number}</td>
                  <td className="px-5 py-3 text-gray-900">{r.payee_name}</td>
                  <td className="px-5 py-3 text-gray-500">
                    {r.fund_pool_description ?? r.fund_pool_code} / {r.project_name}
                  </td>
                  <td className="px-5 py-3 text-gray-500">{r.requested_by_name ?? '—'}</td>
                  <td className="px-5 py-3 text-right text-gray-900">{formatCurrency(r.amount)}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status_name} /></td>
                  <td className="px-5 py-3 text-gray-500">{formatDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">{total} results</p>
          <div className="flex gap-2">
            <button
              onClick={() => setParam('page', String(page - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-xs text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setParam('page', String(page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
